import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PackageOpen, ExternalLink, X, ChevronDown, ChevronUp, CheckCircle2, Clock3 } from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { ProgramaTVSection } from '@/components/ProgramaTVSection';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { parseCreditos } from '@/lib/distribuicao';
import { displayStatus } from '@/lib/types';
import type { Demanda, CotaFamilia, Solicitante, Contrato } from '@/lib/types';

type GrupoKey = CotaFamilia | 'Apex / Research';
type TabKey = GrupoKey | 'Todas' | 'Programa';

const TABS: { label: string; key: TabKey }[] = [
  { label: 'Todas',                  key: 'Todas' },
  { label: 'Conecta Folha Business', key: 'Conecta Folha Business' },
  { label: 'BRM - BUY',             key: 'BRM - BUY' },
  { label: 'Setorial',              key: 'Setorial' },
  { label: 'Apex / Research',        key: 'Apex / Research' },
  { label: 'Programa TV/YouTube',    key: 'Programa' },
];

const GRUPO_COR: Record<GrupoKey, string> = {
  'Conecta Folha Business': 'border-blue/40',
  'BRM - BUY':              'border-purple/40',
  'Setorial':               'border-pink/40',
  'Pesquisa':               'border-green/40',
  'Apex / Research':        'border-green/40',
};

export function Entregas() {
  const [tab, setTab] = useState<TabKey>('Todas');
  const [clienteOpen, setClienteOpen] = useState<string | null>(null);

  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const contratosQ = useQuery({ queryKey: ['contratos'], queryFn: () => api.contratos.list().then((r) => r.data) });
  const cotasQ = useQuery({ queryKey: ['cotas'], queryFn: () => api.cotas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });

  const demandas = demandasQ.data ?? [];
  const contratos = contratosQ.data ?? [];
  const cotas = cotasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];

  const grupoPorSolicitante = useMemo(() => {
    const cotaById = new Map(cotas.map((c) => [c.id, c]));
    const m = new Map<string, GrupoKey>();
    for (const c of contratos) {
      const fam = c.cotaId ? cotaById.get(c.cotaId)?.familia : undefined;
      if (fam) m.set(c.solicitanteId, fam as GrupoKey);
    }
    for (const s of solicitantes) {
      if (m.has(s.id)) continue;
      if (s.tipo === 'Unidade Apex' || s.tipo === 'Time Research') m.set(s.id, 'Apex / Research');
    }
    return m;
  }, [contratos, cotas, solicitantes]);

  const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s])), [solicitantes]);
  const publicadas = demandas.filter((d) => d.status === 'Publicada');

  const blocos = useMemo(() => {
    const byClient = new Map<string, Demanda[]>();
    for (const d of publicadas) {
      const grp = grupoPorSolicitante.get(d.solicitanteId);
      if (tab !== 'Todas' && grp !== tab) continue;
      const arr = byClient.get(d.solicitanteId) ?? [];
      arr.push(d);
      byClient.set(d.solicitanteId, arr);
    }
    return Array.from(byClient.entries())
      .map(([id, dem]) => ({
        solicitanteId: id,
        nome: solById.get(id)?.nome ?? '—',
        grupo: grupoPorSolicitante.get(id),
        total: dem.length,
        demandas: dem.sort((a, b) => (b.dataPublicacao ?? '').localeCompare(a.dataPublicacao ?? '')),
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [publicadas, tab, grupoPorSolicitante, solById]);

  const aberto = clienteOpen ? blocos.find((b) => b.solicitanteId === clienteOpen) : null;

  return (
    <div>
      <PageHeader title="Entregas" />
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={clsx(
                'px-3 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap',
                tab === t.key ? 'text-blue-light border-blue' : 'text-text-2 border-transparent hover:text-text',
              )}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-muted pr-2 whitespace-nowrap">
            {publicadas.length} entregas finalizadas no histórico
          </div>
        </div>

        {tab === 'Programa' ? (
          <ProgramaTVSection />
        ) : (
          <>
            {demandasQ.isLoading && <div className="card text-sm text-muted">Carregando entregas…</div>}

            {!demandasQ.isLoading && blocos.length === 0 && (
              <div className="card text-center py-12">
                <PackageOpen className="w-10 h-10 text-muted mx-auto mb-3" />
                <p className="text-sm text-muted">Sem entregas pra esse filtro.</p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {blocos.map((b) => (
                <button
                  key={b.solicitanteId}
                  type="button"
                  onClick={() => setClienteOpen(b.solicitanteId)}
                  className={clsx(
                    'bg-panel border border-border border-2 rounded-md p-3 text-left hover:border-blue/50 hover:bg-panel-2 transition-colors',
                    b.grupo ? GRUPO_COR[b.grupo] : 'border-border',
                  )}
                >
                  <div className="text-xs font-bold leading-tight line-clamp-2 mb-1.5">{b.nome}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-black text-blue-light">{b.total}</span>
                    <span className="text-[10px] text-muted">{b.total === 1 ? 'peça' : 'peças'}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {aberto && (
        <PecasModal
          nome={aberto.nome}
          demandas={aberto.demandas}
          solicitantes={solicitantes}
          contratos={contratos}
          onClose={() => setClienteOpen(null)}
        />
      )}
    </div>
  );
}

function PecasModal({ nome, demandas, solicitantes, contratos, onClose }: {
  nome: string;
  demandas: Demanda[];
  solicitantes: Solicitante[];
  contratos: Contrato[];
  onClose: () => void;
}) {
  const [abertoId, setAbertoId] = useState<string | null>(null);
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = user?.papel === 'Comercial' || user?.papel === 'Coordenação';
  const [err, setErr] = useState<string | null>(null);
  const solNome = useMemo(() => new Map(solicitantes.map((s) => [s.id, s.nome])), [solicitantes]);
  const ctNome = useMemo(() => new Map(contratos.map((c) => [c.id, c.identificador])), [contratos]);

  const confirmarCredito = useMutation({
    mutationFn: ({ demandaId, ids }: { demandaId: string; ids: string[] }) =>
      api.demandas.confirmarCreditos(demandaId, ids).then((r) => r.data),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['demandas'] }); },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <h2 className="text-sm font-bold">{nome} <span className="text-muted font-normal">· {demandas.length} entregas</span></h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {demandas.map((d) => {
            const expandido = abertoId === d.id;
            const clientes = d.clientesIds ?? [];
            const cts = d.contratosIds ?? (d.contratoId ? [d.contratoId] : []);
            const creditos = new Map(parseCreditos(d.distribuicaoCreditos).map((c) => [c.contratoId, c]));
            return (
              <div
                key={d.id}
                className={clsx('bg-panel-2 border border-border rounded p-2.5 text-xs', expandido && 'md:col-span-2 lg:col-span-3')}
              >
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => setAbertoId(expandido ? null : d.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-text leading-snug line-clamp-2 mb-1.5 text-[11px]">{d.titulo || '(sem título)'}</div>
                    {expandido ? <ChevronUp className="w-3 h-3 text-muted shrink-0" /> : <ChevronDown className="w-3 h-3 text-muted shrink-0" />}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-muted">
                    <span>{d.dataPublicacao ? new Date(d.dataPublicacao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—'}</span>
                    {clientes.length > 0 && <span>{clientes.length} cliente(s)</span>}
                  </div>
                </button>
                {expandido && (
                  <div className="mt-2 pt-2 border-t border-border space-y-2 text-[11px]">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted">
                      <span>Status: <span className="text-text">{displayStatus(d.status)}</span></span>
                      <span>Solicitante: <span className="text-text">{solNome.get(d.solicitanteId) ?? '—'}</span></span>
                      {d.linkFinal && (
                        <a href={d.linkFinal} target="_blank" rel="noreferrer" className="text-blue-light hover:underline inline-flex items-center gap-0.5">
                          peça final <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Clientes que receberam ({clientes.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {clientes.length > 0
                          ? clientes.map((id) => (
                              <span key={id} className="px-1.5 py-0.5 rounded border border-border bg-surface">{solNome.get(id) ?? id}</span>
                            ))
                          : <span className="text-muted">Somente o solicitante original.</span>}
                      </div>
                    </div>
                    {cts.length > 0 && (() => {
                      const aguardando = cts.filter((id) => creditos.get(id)?.status === 'aguardando');
                      const podeCreditar = canManage && d.status === 'Publicada';
                      return (
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="text-[10px] uppercase tracking-wide text-muted">Contratos creditados ({cts.length})</div>
                          {podeCreditar && aguardando.length > 0 && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded border bg-green text-white border-green hover:bg-green/90 cursor-pointer shadow-sm active:scale-[0.98] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={confirmarCredito.isPending}
                              onClick={() => confirmarCredito.mutate({ demandaId: d.id, ids: aguardando })}
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              Confirmar créditos ({aguardando.length})
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cts.map((id) => {
                            const cr = creditos.get(id);
                            const aguardandoEste = cr?.status === 'aguardando';
                            return (
                              <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border bg-surface">
                                {ctNome.get(id) ?? id}
                                {cr?.status === 'confirmado'
                                  ? <span title={`Crédito confirmado por ${cr.confirmadoPor ?? '—'}`}><CheckCircle2 className="w-3 h-3 text-green" /></span>
                                  : cr
                                    ? <span title="Crédito aguardando confirmação"><Clock3 className="w-3 h-3 text-yellow" /></span>
                                    : null}
                                {podeCreditar && aguardandoEste && (
                                  <button
                                    type="button"
                                    className="text-[10px] font-semibold text-green underline underline-offset-2 hover:no-underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={confirmarCredito.isPending}
                                    onClick={() => confirmarCredito.mutate({ demandaId: d.id, ids: [id] })}
                                  >
                                    creditar
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                        {podeCreditar && aguardando.length > 0 && (
                          <p className="text-[10px] text-muted mt-1">Clique em “creditar” (ou “Confirmar créditos”) para confirmar o crédito no contrato.</p>
                        )}
                        {err && <p className="text-[10px] text-red mt-1">{err}</p>}
                      </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
